import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Construction Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Construction was one
 * of the fifteen still carrying it: a two-paragraph series description under an
 * amber note saying the sector's own words had never been supplied. They were
 * supplied on 2026-08-27, *"construction focus"*, with four attachments — a
 * full-page screenshot of digitopia.co's Construction Industry Focus page as the
 * reference, the cover at 2560px, Figure 1 and the report mockup. So this is the
 * ninth designed report page and the fifth focus cut to arrive with **both** of
 * its figures as files.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference screenshot — one full-page capture, which renders body copy down
 * to a few pixels. The *shape* of the page is certain; the *wording* is a careful
 * reading. On 2026-08-28 the same page came back as **four** captures at reading
 * size (*"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*), which
 * settled the two layout questions the first pass had left open — the live hero does
 * carry the cover and the live analysis band does carry the mockup, so both notes
 * below are now only about the wording. The prose still wants a reading, because
 * four captures of a page is not the page's text.
 * Every band that carries prose therefore carries a `check`, exactly as
 * `content/daimiBanking.ts` and `content/daimiAirlines.ts` do, and
 * `daimiConstructionChecks()` feeds them into the same manifest as the rest of the
 * site's amber notes so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its five Download buttons open
 * an embedded HubSpot form. These documents are prerendered with no client bundle
 * at all (see `src/prerender.tsx`), so an embedded form script would never run —
 * and the other reports on this site hand the reader the PDF from our own bucket
 * instead. So every Download button here is that PDF, and `DAIMI_CN_DOWNLOAD.check`
 * is what keeps the question of gating open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working.
 */
export const DAIMI_CN_SLUG = 'digital-and-ai-maturity-report-2026-construction-industry'

/**
 * The hero's two paragraphs, read off the reference screenshot.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 */
export const DAIMI_CN_INTRO = {
  body: [
    'Construction remains operationally complex and digitally fragmented, relying on manual coordination across subcontractor networks. However, rising costs and labor constraints are forcing a shift from reactive delivery to predictable value.',
    'To lead in 2026, firms must treat digital and AI as a unified production system by modernizing core architectures and unifying project data. Success requires embedding AI directly into planning and site execution to ensure consistent performance across the portfolio.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card
   * in the reports grid carries — and the file attached with this request is the
   * same artwork at 2560px, so it replaced it under the same key rather than
   * landing beside it under a second name. The index card reads the same path and
   * simply gets a sharper cover out of it. Same call the Airlines, Automotive and
   * Banking cuts made.
   */
  photo: {
    src: '/photo/daimi-report-2026-construction-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Construction Focus, February 2026',
  } satisfies Photo,
  check:
    'These two paragraphs are transcribed from the reference captures rather than supplied as text, so they want reading against the live page once before production. The cover beside them is settled: the captures sent on 2026-08-28 show it in the live hero at exactly this size, which is what the first pass could not tell and had asked about here.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by four lines that each open with a
 * bolded label — "Core Assets:", "The Scaling Barrier:", "The Data Gap:",
 * "Industrializing AI:". Split into `label` and `body` here so the label can be
 * set rather than emphasised inside a sentence, which is what a `<strong>` in the
 * middle of a paragraph gives. Four points — the longest findings band of any
 * focus cut on this site so far.
 */
export const DAIMI_CN_FINDINGS = {
  heading: 'DAIMI Findings: Discipline vs. Fragmentation',
  lede: 'The construction sector demonstrates a maturity pattern where agility (Velocity) and foundational resilience (Endurance) move together, though the industry generally sits at a mid-level maturity with significant dispersion.',
  points: [
    {
      label: 'Core Assets',
      body: 'Maturity is strongest in operational discipline and risk management, driven by non-negotiable safety and compliance requirements.',
    },
    {
      label: 'The Scaling Barrier',
      body: 'Progress is frequently stalled by project fragmentation, where data is trapped in unstructured documents spread across owners, designers, and subcontractors.',
    },
    {
      label: 'The Data Gap',
      body: 'Disconnected tools for procurement, site reporting, and safety logs limit visibility into schedule and cost, keeping decision-making reactive.',
    },
    {
      label: 'Industrializing AI',
      body: 'While many see “local wins” on specific projects, the next step requires building repeatable delivery capabilities supported by integrated platforms.',
    },
  ],
  /**
   * Figure 1 itself, which is the file supplied with this request — the vitality
   * scatter the Airlines cut carries, not the six-year study-results chart the
   * Automotive and Banking cuts do. It is 1200×1185 on a white ground, so it gets
   * a white card of its own and a floor under it: seventy-six marks packed inside
   * one unit of each axis close up completely at phone width.
   */
  photo: {
    src: '/photo/daimi-construction-vitality-chart.webp',
    alt: 'DAIMI Vitality Chart for the construction industry: 76 companies plotted on Digital Velocity against Digital Endurance, clustered between 2.0 and 3.5 on both axes',
  } satisfies Photo,
  /**
   * The caption, punctuated with a colon rather than a dash — asked for on
   * 2026-08-30, no dashes on the sector pages. The live page separates the two
   * halves with a dash; six of the eight focus cuts already read
   * "…Study Results: <Sector> Industry Score…", so a colon is the house
   * punctuation here and the wording is otherwise untouched.
   */
  caption: 'Figure 1: DAIMI Vitality Chart: Construction Industry',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole; its own furniture says “Company Count 76”, which is the count this page therefore states in the alt text and nowhere else — the live caption does not carry one, unlike the Airlines page’s. What wants a reading against digitopia.co is the prose: the lede and the four findings are transcribed from the screenshot, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_CN_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Construction Industry',
  body: [
    'To lead in 2026, construction executives must move beyond project-by-project experiments to a portfolio-scale roadmap. Success requires modernizing the core systems that run project delivery and finance to unlock real-time visibility. By upskilling site leaders and fostering data interoperability with the broader ecosystem, firms can turn risk discipline into a sustained, AI-powered competitive advantage.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn construction complexity into predictable performance.',
  ],
  /** The mockup supplied with this request — the construction cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-construction-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Construction Focus',
  } satisfies Photo,
  check:
    'The paragraphs are transcribed from the reference captures rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. The mockup beside them is settled: the captures sent on 2026-08-28 show it on the right of this band on the live page, which is where it sits here.',
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut. They
 * are `content/reportClosing.ts` now, drawn by `components/ReportClosing.tsx` on
 * every report page but three — asked for on 2026-08-28, *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"*. The assessment-count
 * question those notes raised is asked once there instead of once per cut.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_CN_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Construction Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiBankingChecks()` use,
 * and appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures. Three of the four are a transcription nobody has
 * read against the original, and the fourth is a decision about gating that belongs
 * to marketing. The two layout questions the first pass raised — the hero cover and
 * the analysis mockup — are gone, because the 2026-08-28 captures answered them.
 */
export function daimiConstructionChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_CN_INTRO.check },
    { section: 'findings', check: DAIMI_CN_FINDINGS.check },
    { section: 'analysis', check: DAIMI_CN_ANALYSIS.check },
    { section: 'download', check: DAIMI_CN_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_CN_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
